// src/utils/toast-message.ts

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastOptions {
    message: string;
    type?: ToastType;
    container?: HTMLDivElement | null;
    duration?: number;
    onClick?: () => void;
    onDismiss?: () => void;
}

// 1. ENCAPSULATION: Keep these private! No 'export' keyword.
let toastNotificationTemplate: HTMLTemplateElement | null = null;
let globalToastContainer: HTMLDivElement | null = null;

// Call this ONCE in main.ts
export function initToastMessageService() {
    toastNotificationTemplate = document.getElementById("template_toastNotification") as HTMLTemplateElement | null;
    globalToastContainer = document.querySelector('div.scw-toast-container[data-toast-notification-container][data-toast-notification-container-global]');

    // Fail loudly during development if the DOM is missing the required elements
    if (!toastNotificationTemplate) console.warn('ToastMessageService: Template not found in DOM.');
    if (!globalToastContainer) console.warn('ToastMessageService: Global container not found in DOM.');
}

export function showToast({
                              message,
                              type = 'info',
                              container,
                              duration,
                              onDismiss,
                          }: ToastOptions) {

    // 2. AVOID SILENT FAILURES: Let the developer know WHY it isn't showing
    if (!toastNotificationTemplate) {
        console.warn(`Toast dropped ("${message}"). Service not initialized or template missing.`);
        return;
    }

    const fragment = toastNotificationTemplate.content.cloneNode(true) as DocumentFragment;

    const notification = fragment.querySelector<HTMLDivElement>('div[data-toast-wrapper]');
    const messageEl = notification?.querySelector<HTMLSpanElement>('span[data-toast-msg]');
    const clsButton = notification?.querySelector<HTMLButtonElement>('button[data-toast-close]');

    if (!notification) return;

    if (messageEl) {
        messageEl.textContent = message;
    }

    notification.classList.add(`scw-toast--${type}`);

    let timeoutId: number;

    const dismissToast = () => {
        if (!notification.isConnected) return;

        if (timeoutId) clearTimeout(timeoutId);

        notification.remove();

        if (onDismiss) {
            onDismiss();
        }
    };

    clsButton?.addEventListener('click', (event) => {
        event.preventDefault();
        dismissToast();
    });

    if (duration) {
        timeoutId = window.setTimeout(() => {
            dismissToast();
        }, duration);
    }

    const targetContainer = container || globalToastContainer;

    if (targetContainer) {
        const maxToasts = 3;
        if (targetContainer.children.length >= maxToasts) {
            targetContainer.firstElementChild?.remove();
        }
        
        targetContainer.appendChild(notification);
    } else {
        console.warn('Toast dropped: No target container found to append the notification.');
    }
}

export const toast = {
    success: (msg: string, container?: HTMLDivElement) => showToast({message: msg, type: 'success', container}),
    error: (msg: string, container?: HTMLDivElement) => showToast({
        message: msg,
        type: 'error',
        container,
    }),
    warning: (msg: string, container?: HTMLDivElement) => showToast({message: msg, type: 'warning', container}),
    info: (msg: string, container?: HTMLDivElement) => showToast({message: msg, type: 'info', container}),
};